"use client";

import { AlertTriangle } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface MessageErrorBoundaryProps {
  children: ReactNode;
  /** Message id, included in the logged error so a looping message is
   *  identifiable. */
  messageId: string;
}

interface MessageErrorBoundaryState {
  hasError: boolean;
}

/**
 * Per-message render error boundary.
 *
 * A render-phase throw (notably React error #185, "Maximum update depth
 * exceeded") inside a single message's parts would otherwise bubble to the
 * app-wide boundary and blank the whole chat. Wrapping each message row
 * isolates the failure to the offending message (a small inline notice)
 * instead of taking down the entire transcript.
 *
 * Port of the private `@magnus/react` boundary minus its telemetry beacon —
 * beacon emission stays host-side (the flagship wrapper keeps its own
 * effect). Hosts should listen for the console error / their own boundary
 * telemetry.
 *
 * Must be a class component — only `componentDidCatch` /
 * `getDerivedStateFromError` can intercept a render-phase throw.
 */
export class MessageErrorBoundary extends Component<
  MessageErrorBoundaryProps,
  MessageErrorBoundaryState
> {
  state: MessageErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): MessageErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(
      `[magnus-chat] message ${this.props.messageId} failed to render`,
      error,
      errorInfo.componentStack
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="my-2 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-muted-foreground text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <span>This message couldn&apos;t be displayed.</span>
        </div>
      );
    }
    return this.props.children;
  }
}
